'use client';
import { createContext, useContext, useEffect, useState, PropsWithChildren } from 'react';
import { useSWRConfig } from 'swr';
import { getConfig, getInitials } from '@fshr/shared-react';
import {
	validateReauthOtp,
	requestEmailValidationCode as _requestEmailValidationCode,
	submitEmailValidationCode as _submitEmailValidationCode,
	validateEmailOtp,
	validatePhoneOtp,
	logout,
	ServerActionError,
} from '@fshr/shared-react/server';

import type { ProfileV1 } from '@fshr/microservices/profile';
import { AuthenticationV1 } from '@fshr/microservices/authentication';
import { ERROR_CODES } from '@fshr/shared';
import { GlobalStore } from '@fshr/stores';
import { useParams, usePathname } from 'next/navigation';
type SubmitReauthOtpArgs = Omit<AuthenticationV1.ValidateReauthOtpRequest, 'UserToken'>;
type SubmitPhoneOtpArgs = Omit<AuthenticationV1.ValidatePhoneOtpRequest, 'UserToken'>;
type SubmitEmailOtpArgs = Omit<AuthenticationV1.ValidateEmailOtpRequest, 'UserToken'>;

const ProfileContext = createContext<
	| {
			profile: ProfileV1.ProfileResponse | null;
			isInitializing: boolean;
			isLoadingProfile: boolean;
			isAuthenticated: boolean;
			isFullyAuthenticated: boolean;
			isPersistedAuthenticated: boolean;
			setProfile: (profile: ProfileV1.ProfileResponse) => void;
			submitReauthOtp: (args: SubmitReauthOtpArgs) => Promise<ProfileV1.ProfileResponse>;
			submitEmailOtp: (args: SubmitEmailOtpArgs) => Promise<ProfileV1.ProfileResponse>;
			submitPhoneOtp: (args: SubmitPhoneOtpArgs) => Promise<ProfileV1.ProfileResponse>;
			signOut: () => Promise<ProfileV1.Empty>;
			name: string;
			initials: string;
			isEmailValidationLoading: boolean;
			emailValidationErrorCode: string | null;
			requestEmailValidationCode: (salesForceId: string, email: string) => Promise<void>;
			submitEmailValidationCode: (email: string, validationCode: string) => Promise<void>;
			globalStore?: GlobalStore;
	  }
	| undefined
>(undefined);

const cacheClearWhitelist = [
	'for-you',
	'header',
	'interests',
	'itinerary',
	'membership-card',
	'memberships',
	'profile-page',
	'upcoming-trip',
	'webchat',
];

export default function ProfileProvider({ children }: PropsWithChildren) {
	const { NEXT_PUBLIC_BFF_BASE_URL } = getConfig();

	const [isInitializing, setIsInitializing] = useState(true); // will do full screen loading indicator
	const [isLoadingProfile, setIsInitializingProfile] = useState(true);
	const [profile, _setProfile] = useState<ProfileV1.ProfileResponse | null>(null);
	const [globalStore, setGlobalStore] = useState<GlobalStore | undefined>(undefined);
	const { hotelCode, confirmationNumber } = useParams();
	const pathname = usePathname();

	const isAuthenticated = Boolean(profile?.uniqueId);
	const isFullyAuthenticated = isAuthenticated && !profile?.dataRedacted;
	const isPersistedAuthenticated = isAuthenticated && Boolean(profile?.dataRedacted);
	const name = [profile?.name?.firstName, profile?.name?.surname].filter(Boolean).join(' ');
	const initials = getInitials(name);

	const { mutate } = useSWRConfig();

	const [isEmailValidationLoading, setIsEmailValidationLoading] = useState(false);
	const [emailValidationErrorCode, setEmailValidationErrorCode] = useState<string | null>(null);

	const clearSWR = () => {
		mutate(
			key => {
				return (
					typeof key === 'string' &&
					cacheClearWhitelist.some(whitelistKey => key.includes(whitelistKey))
				);
			}, // which cache keys are updated
			undefined, // update cache data to `undefined`
			{ revalidate: false } // do not revalidate
		);
	};

	const setProfile = (newProfile: ProfileV1.ProfileResponse | null, clearCache = false) => {
		_setProfile(newProfile);
		if (!globalStore) {
			setGlobalStore(new GlobalStore(hotelCode?.toString(), confirmationNumber?.toString()));
		} else {
			globalStore.refreshState();
		}
		if (clearCache) clearSWR();
	};
	// FUNCTIONS
	function fetchProfile() {
		const controller = new AbortController();
		const signal = controller.signal;

		setIsInitializingProfile(true);
		const request = fetch(`${NEXT_PUBLIC_BFF_BASE_URL}/profile/`, {
			headers: { 'Content-Type': 'application/json' },
			credentials: 'include',
			signal,
		})
			.then(response => response.json())
			.then(responseJson => {
				setProfile(responseJson);
				setIsInitializingProfile(false);
				return responseJson;
			})
			.catch(error => {
				if (error.name !== 'AbortError') {
					setIsInitializingProfile(false);
				}

				throw error;
			});

		return {
			request,
			abort: () => controller.abort(),
		};
	}

	async function submitReauthOtp({ loginType, otp }: SubmitReauthOtpArgs) {
		const [error, profile] = await validateReauthOtp(loginType, otp);

		if (error) {
			throw error;
		}

		setProfile(profile);
		return profile;
	}

	async function submitPhoneOtp({ otp, persistLogin = false, phone }: SubmitPhoneOtpArgs) {
		const [error, profile] = await validatePhoneOtp(phone, otp, persistLogin);

		if (error) {
			throw error;
		}

		setProfile(profile, true);
		return profile;
	}

	async function submitEmailOtp({ otp, persistLogin = false, email }: SubmitEmailOtpArgs) {
		const [error, profile] = await validateEmailOtp(email, otp, persistLogin);

		if (error) {
			throw error;
		}

		setProfile(profile, true);
		return profile;
	}

	async function signOut() {
		setIsInitializing(true);

		const [error] = await logout();
		if (error) {
			throw error;
		}
		setProfile(null, true);
		setIsInitializing(false);
		return {};
	}

	// USE_EFFECTS
	// fetch profile on load - might need to refetch every 20 minutes
	useEffect(() => {
		setIsInitializing(true);
		const { request, abort } = fetchProfile();
		request
			.then(() => {
				setIsInitializing(false);
			})
			.catch(error => {
				if (error.name !== 'AbortError') {
					setIsInitializing(false);
				}
			});

		return () => {
			abort();
		};
	}, []);

	useEffect(() => {
		if (globalStore) {
			if (
				!confirmationNumber ||
				confirmationNumber !== globalStore.currentConfirmationNumber ||
				!hotelCode ||
				hotelCode !== globalStore.currentProperty
			) {
				globalStore.setCurrentConfirmationNumber(confirmationNumber?.toString() || null);
				globalStore.setCurrentProperty(hotelCode?.toString() || null);
			}
		}
	}, [globalStore, hotelCode, confirmationNumber, pathname]);

	const requestEmailValidationCode = async (salesForceId: string, email: string) => {
		setIsEmailValidationLoading(true);
		setEmailValidationErrorCode(null);
		try {
			const [error] = await _requestEmailValidationCode(salesForceId, email);
			if (error) {
				throw error;
			}
		} catch (e) {
			setEmailValidationErrorCode(
				(e as ServerActionError)?.errorCode || ERROR_CODES.UNKNOWN_ERROR_CODE
			);
		} finally {
			setIsEmailValidationLoading(false);
		}
	};

	const submitEmailValidationCode = async (email: string, validationCode: string) => {
		setIsEmailValidationLoading(true);
		setEmailValidationErrorCode(null);
		try {
			const [error, updatedProfile] = await _submitEmailValidationCode(email, validationCode);
			if (error) {
				throw error;
			}
			setIsEmailValidationLoading(false);
			setProfile(updatedProfile);
		} catch (e) {
			setIsEmailValidationLoading(false);
			setEmailValidationErrorCode(
				(e as ServerActionError)?.errorCode || ERROR_CODES.UNKNOWN_ERROR_CODE
			);
			throw e;
		}
	};

	return (
		<ProfileContext.Provider
			value={{
				profile,
				isInitializing,
				isLoadingProfile,
				isAuthenticated,
				isFullyAuthenticated,
				isPersistedAuthenticated,
				name,
				initials,
				setProfile,
				submitReauthOtp,
				submitEmailOtp,
				submitPhoneOtp,
				requestEmailValidationCode,
				submitEmailValidationCode,
				isEmailValidationLoading,
				emailValidationErrorCode,
				signOut,
				globalStore,
			}}
		>
			{children}
		</ProfileContext.Provider>
	);
}

const useProfile = () => {
	const context = useContext(ProfileContext);
	if (context === undefined) {
		throw new Error('useProfile must be used within a ProfileProvider');
	}
	return context;
};

export { ProfileProvider, useProfile };
